/**
 * cart-upsell.js
 * ------------------------------------------------------------------
 * Le HTML de base (meilleures ventes) est déjà rendu côté serveur
 * par cart-upsell.liquid — l'affichage ne dépend donc JAMAIS de ce
 * script. Ce fichier fait deux choses :
 *  1. Branche le clic "Ajouter" sur les cartes déjà présentes.
 *  2. Tente, en arrière-plan, de récupérer des suggestions plus
 *     pertinentes (produits complémentaires) via l'API Shopify ; en
 *     cas de succès seulement, elles REMPLACENT le contenu de base.
 *     En cas d'échec, le contenu de base reste affiché tel quel.
 * ------------------------------------------------------------------
 */

class CartUpsellRecommendations extends HTMLElement {
  connectedCallback() {
    this.productId = this.dataset.productId;
    this.limit = this.dataset.limit || 4;
    this.excludeIds = (this.dataset.exclude || '')
      .split(',')
      .filter(Boolean)
      .map((id) => id.trim());

    // Branche les cartes déjà rendues par Liquid (meilleures ventes).
    this.bindCards();

    // Tente une amélioration en arrière-plan, sans jamais bloquer ni
    // vider l'affichage existant en cas d'échec.
    if (this.productId) this.tryEnhance();
  }

  async tryEnhance() {
    try {
      let products = await this.fetchRecommendations('complementary');

      if (products.length < 3) {
        const related = await this.fetchRecommendations('related');
        const existingIds = new Set(products.map((p) => p.id));
        related.forEach((p) => {
          if (!existingIds.has(p.id)) products.push(p);
        });
      }

      products = products
        .filter((p) => !this.excludeIds.includes(String(p.id)))
        .slice(0, Number(this.limit));

      // Rien de mieux à proposer : on garde le rendu Liquid existant.
      if (products.length === 0) return;

      this.render(products);
    } catch (error) {
      // Échec silencieux : le contenu Liquid déjà affiché suffit.
      console.error('cart-upsell: amélioration impossible, contenu de base conservé', error);
    }
  }

  async fetchRecommendations(intent) {
    const url = `${window.Shopify.routes.root}recommendations/products.json?product_id=${this.productId}&limit=10&intent=${intent}`;
    try {
      const response = await fetch(url);
      const data = await response.json();
      return data.products || [];
    } catch (error) {
      return [];
    }
  }

  render(products) {
    const money = (cents) => this.formatMoney(cents);

    const cards = products
      .map((product) => {
        const variant = product.variants.find((v) => v.available) || product.variants[0];
        const image = product.featured_image ? product.featured_image : product.images[0];
        const disabled = !variant || !variant.available;

        return `
          <div class="cart-upsell__card" data-product-card data-variant-id="${variant ? variant.id : ''}">
            <div class="cart-upsell__card-media">
              ${
                image
                  ? `<img src="${this.resizeImage(image, 300)}" alt="${this.escapeHtml(product.title)}" loading="lazy" width="300" height="300">`
                  : ''
              }
            </div>
            <div class="cart-upsell__card-body">
              <p class="cart-upsell__card-title">${this.escapeHtml(product.title)}</p>
              <button
                type="button"
                class="cart-upsell__add"
                aria-label="Ajouter ${this.escapeHtml(product.title)} au panier"
                ${disabled ? 'disabled' : ''}
              >
                <span class="cart-upsell__add-label">Ajouter</span>
                <span class="cart-upsell__add-price">/ ${money(variant ? variant.price : product.price)}</span>
                <span class="cart-upsell__spinner" aria-hidden="true"></span>
              </button>
            </div>
          </div>
        `;
      })
      .join('');

    this.innerHTML = `
      <div class="cart-upsell__row-wrap">
        <div class="cart-upsell__row">${cards}</div>
      </div>
    `;

    this.bindCards();
  }

  bindCards() {
    this.querySelectorAll('[data-product-card]').forEach((card) => {
      const button = card.querySelector('.cart-upsell__add');
      if (button && !button.disabled && button.dataset.bound !== 'true') {
        button.dataset.bound = 'true';
        button.addEventListener('click', () => this.quickAdd(card, button));
      }
    });
  }

  async quickAdd(card, button) {
    const variantId = card.dataset.variantId;
    if (!variantId || button.classList.contains('is-loading')) return;

    button.classList.add('is-loading');
    button.disabled = true;

    try {
      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
      });

      if (!response.ok) throw new Error('add.js a échoué');

      await this.refreshCartSections();
    } catch (error) {
      console.error('cart-upsell: échec de l’ajout rapide', error);
      button.classList.remove('is-loading');
      button.disabled = false;
    }
  }

  async refreshCartSections() {
    try {
      const response = await fetch(window.location.href, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });
      if (!response.ok) throw new Error(`Réponse HTTP ${response.status}`);

      const html = await response.text();
      const freshDoc = new DOMParser().parseFromString(html, 'text/html');

      this.swapNode('#CartDrawer', freshDoc);
      this.swapNode('cart-items', freshDoc);
      this.swapNode('#cart-icon-bubble', freshDoc);

      document.dispatchEvent(
        new CustomEvent('cart:refresh', { bubbles: true, detail: { source: 'cart-upsell' } })
      );
    } catch (error) {
      console.error('cart-upsell : échec du rafraîchissement du panier', error);
    }
  }

  swapNode(selector, freshDoc) {
    const current = document.querySelector(selector);
    const fresh = freshDoc.querySelector(selector);
    if (!current || !fresh) return false;

    const preservedClasses = [...current.classList];
    const hadOpenAttr = current.hasAttribute('open');

    current.replaceWith(fresh);
    fresh.classList.add(...preservedClasses);
    if (hadOpenAttr) fresh.setAttribute('open', '');

    return true;
  }

  resizeImage(url, size) {
    if (!url) return '';
    return url.includes('?') ? `${url}&width=${size}` : `${url}?width=${size}`;
  }

  formatMoney(cents) {
    const format = (this.dataset.moneyFormat || '{{amount}} €').replace(/<[^>]*>/g, '');
    const placeholderMatch = format.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!placeholderMatch) return format;

    const withDelimiters = (number, precision, thousands, decimal) => {
      number = (number / 100).toFixed(precision);
      const parts = number.split('.');
      const whole = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
      const decimals = parts[1] ? decimal + parts[1] : '';
      return whole + decimals;
    };

    let value;
    switch (placeholderMatch[1]) {
      case 'amount_no_decimals':
        value = withDelimiters(cents, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        value = withDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = withDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_space_separator':
        value = withDelimiters(cents, 2, ' ', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = withDelimiters(cents, 0, ' ', ',');
        break;
      case 'amount':
      default:
        value = withDelimiters(cents, 2, ',', '.');
        break;
    }

    return format.replace(placeholderMatch[0], value);
  }

  escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }
}

customElements.define('cart-upsell-recommendations', CartUpsellRecommendations);
